import React from 'react';
import { Thermometer, Droplet, Wind, Sun, Clock, Signal, MapPin, Eye, BarChart3, Battery, Plug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '../ui';
import { useLanguageNavigation } from '../routing/LanguageWrapper';
import './StationPopup.css';

function getSignalQuality(dbm) {
  if (dbm > -70) return { quality: 'excellent', color: '#5A7A3A', width: '100%' };
  if (dbm > -80) return { quality: 'good', color: '#7A8A5E', width: '75%' };
  if (dbm > -90) return { quality: 'fair', color: '#8A7A4A', width: '50%' };
  return { quality: 'poor', color: '#8A4A3A', width: '25%' };
}

function getPowerSource(batteryLevel) {
  if (batteryLevel !== undefined && batteryLevel !== null && batteryLevel > 0) {
    return { type: 'battery', value: batteryLevel };
  }
  return { type: 'grid', value: null };
}

export function StationPopup({ station }) {
  const { t } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  const signal = getSignalQuality(station.signalStrength);
  const powerSource = getPowerSource(station.batteryLevel);

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return t('stations.time.secondsAgo', { count: seconds });
    if (seconds < 3600) return t('stations.time.minutesAgo', { count: Math.floor(seconds / 60) });
    if (seconds < 86400) return t('stations.time.hoursAgo', { count: Math.floor(seconds / 3600) });
    return t('stations.time.daysAgo', { count: Math.floor(seconds / 86400) });
  };
  
  const handleViewDetails = () => {
    // Navigate to stations page with state to open view modal
    navigateTo('/stations', { state: { stationId: station.id, openViewModal: true } });
  };
  
  const handleViewAnalytics = () => {
    // Navigate to analytics page with stationId
    navigateTo('/analytics', { state: { stationId: station.id } });
  };
  
  return (
    <div className="station-popup">
      {/* Header */}
      <div className="station-popup__header">
        <div>
          <div className="station-popup__code">{station.stationCode}</div>
          <Badge 
            variant={station.sensorType === 'campo_aperto' ? 'success' : 'info'}
            size="sm"
            dot
          >
            {station.sensorType === 'campo_aperto'
              ? t('stations.types.campo_aperto')
              : t('stations.types.retro_modulo')}
          </Badge>
        </div>
        <Badge 
          variant={
            station.status === 'active' ? 'success' :
            station.status === 'warning' ? 'warning' : 'danger'
          }
          size="sm"
        >
          {t(`stations.status.${station.status}`)}
        </Badge>
      </div>
      
      {/* Metrics Grid */}
      {station.currentReadings && (
        <div className="station-popup__metrics">
          <div className="station-popup__metric">
            <div className="station-popup__metric-icon" style={{ color: '#8A4A3A' }}>
              <Thermometer size={16} />
            </div>
            <div className="station-popup__metric-label">{t('analytics.metrics.temperature')}</div>
            <div className="station-popup__metric-value">
              {station.currentReadings.temperature}°C
            </div>
          </div>
          
          <div className="station-popup__metric">
            <div className="station-popup__metric-icon" style={{ color: '#4A7A8A' }}>
              <Droplet size={16} />
            </div>
            <div className="station-popup__metric-label">{t('analytics.metrics.humidity')}</div>
            <div className="station-popup__metric-value">
              {station.currentReadings.humidity}%
            </div>
          </div>
          
          <div className="station-popup__metric">
            <div className="station-popup__metric-icon" style={{ color: '#6B6B6B' }}>
              <Wind size={16} />
            </div>
            <div className="station-popup__metric-label">{t('analytics.metrics.windSpeed')}</div>
            <div className="station-popup__metric-value">
              {station.currentReadings.windSpeed} m/s
            </div>
          </div>
          
          <div className="station-popup__metric">
            <div className="station-popup__metric-icon" style={{ color: '#8A7A4A' }}>
              <Sun size={16} />
            </div>
            <div className="station-popup__metric-label">{t('analytics.metrics.radiation')}</div>
            <div className="station-popup__metric-value">
              {station.currentReadings.solarRadiation} W/m²
            </div>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <div className="station-popup__footer">
        <div className="station-popup__info">
          <div className="station-popup__info-item">
            <Clock size={14} />
            <span>{t('stations.updated', { time: formatTimeAgo(station.lastTransmission) })}</span>
          </div>
          
          {/* Power Source */}
          <div className="station-popup__info-item">
            {powerSource.type === 'battery' ? (
              <>
                <Battery size={14} style={{ color: '#4A7A8A' }} />
                <span style={{ fontSize: '11px', color: '#4A7A8A' }}>{powerSource.value}%</span>
              </>
            ) : (
              <>
                <Plug size={14} style={{ color: '#6B6B6B' }} />
                <span style={{ fontSize: '11px', color: '#6B6B6B' }}>{t('stations.power.gridPowered')}</span>
              </>
            )}
          </div>
          
          {station.signalStrength !== -999 && station.signalStrength !== undefined && (
            <div className="station-popup__info-item">
              <Signal size={14} />
              <div className="station-popup__signal-bar">
                <div 
                  className="station-popup__signal-fill"
                  style={{ width: signal.width, backgroundColor: signal.color }}
                />
              </div>
              <span>{Math.round(station.signalStrength)} dBm</span>
            </div>
          )}
          
          {station.locationDescription && (
            <div className="station-popup__info-item">
              <MapPin size={14} />
              <span>{station.locationDescription}</span>
            </div>
          )}
        </div>
        
        <div className="station-popup__actions">
          <Button 
            variant="secondary" 
            size="sm" 
            className="station-popup__action"
            icon={<Eye size={14} />}
            onClick={handleViewDetails}
          >
            {t('stations.actions.viewDetails')}
          </Button>
          <Button 
            variant="secondary" 
            size="sm" 
            className="station-popup__action"
            icon={<BarChart3 size={14} />}
            onClick={handleViewAnalytics}
          >
            {t('stations.actions.viewAnalytics')}
          </Button>
        </div>
      </div>
    </div>
  );
}